import {
  IonAlert,
  IonButton,
  IonContent,
  IonFooter,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTextarea,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Header from "src/components/Header";
import { enviarRespuestaTardanza } from "src/services/alertasTardanza";
import {
  getMotivosAusencia,
  MOTIVO_AUSENCIA_OTRO,
  MOTIVOS_AUSENCIA_FALLBACK,
} from "src/services/gestionAusencia";

type OpcionRespuesta = {
  codigo: number;
  texto: string;
};

const OPCIONES: OpcionRespuesta[] = [
  { codigo: 1, texto: "Estoy en el Contrato" },
  { codigo: 2, texto: "Estoy en camino" },
  { codigo: 3, texto: "No voy a asistir" },
  { codigo: 4, texto: "No corresponde asignacion" },
];

// "No voy a asistir" es la unica opcion que pide motivo. Sin el, la ausencia
// queda registrada sin explicacion y nadie puede saber despues por que falto.
const CODIGO_NO_ASISTIR = 3;

const parseNumber = (value: string | null) => {
  if (!value) return undefined;
  const numero = Number(value);
  return Number.isFinite(numero) ? numero : undefined;
};

const NotificacionTardanza: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const location = useLocation();
  const [enviando, setEnviando] = useState(false);
  const [respuestaEnviada, setRespuestaEnviada] = useState(false);
  const [confirmarCerrar, setConfirmarCerrar] = useState(false);

  // Paso extra para "no voy a asistir": se elige la opcion, se pide el motivo
  // y recien ahi se envia.
  const [opcionPendiente, setOpcionPendiente] = useState<OpcionRespuesta | null>(null);
  const [motivoAusencia, setMotivoAusencia] = useState("");
  const [motivoTexto, setMotivoTexto] = useState("");
  const [motivos, setMotivos] = useState<string[]>(MOTIVOS_AUSENCIA_FALLBACK);
  const [motivoOtro, setMotivoOtro] = useState(MOTIVO_AUSENCIA_OTRO);

  const datos = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      alerta_id: parseNumber(params.get("alerta_id") || params.get("id_alerta")),
      gestion_id: parseNumber(params.get("gestion_id")),
      asignacion_id: parseNumber(params.get("asignacion_id")),
      ps_objeto: params.get("ps_objeto") || undefined,
      ps_nombre: params.get("ps_nombre") || undefined,
      fecha: params.get("fecha") || undefined,
      hora_asignada: params.get("hora_asignada") || undefined,
      cod_operacion: params.get("cod_operacion") || undefined,
      op_cedula: params.get("op_cedula") || undefined,
      op_nombre: params.get("op_nombre") || undefined,
    };
  }, [location.search]);

  // El catalogo lo define EMSYS. Se pide una sola vez al abrir la pantalla; si
  // falla, getMotivosAusencia ya devuelve la lista de respaldo.
  useEffect(() => {
    let vigente = true;
    getMotivosAusencia().then((catalogo) => {
      if (!vigente) return;
      setMotivos(catalogo.motivos);
      setMotivoOtro(catalogo.motivo_otro);
    });
    return () => {
      vigente = false;
    };
  }, []);

  const descripcion = useMemo(() => {
    const partes = [] as string[];
    if (datos.ps_nombre || datos.ps_objeto) {
      partes.push(`Punto: ${datos.ps_nombre || datos.ps_objeto}`);
    }
    if (datos.hora_asignada) {
      partes.push(`Hora asignada: ${datos.hora_asignada}`);
    }
    if (datos.fecha) {
      partes.push(`Fecha: ${datos.fecha}`);
    }
    return partes.join(" | ");
  }, [datos]);

  const enviarRespuesta = async (
    opcion: OpcionRespuesta,
    extra?: { motivo_ausencia?: string; motivo_texto?: string }
  ) => {
    if (enviando || respuestaEnviada) return;
    setEnviando(true);

    try {
      const resultado = await enviarRespuestaTardanza({
        ...datos,
        ...(extra || {}),
        respuesta_codigo: opcion.codigo,
        respuesta_texto: opcion.texto,
        origen: "push",
      });
      setRespuestaEnviada(true);

      if (!resultado.n8nOk && process.env.REACT_APP_N8N_WEBHOOK_URL) {
        present({
          message: "Respuesta registrada, pero no se pudo notificar a n8n.",
          duration: 4000,
          color: "warning",
        });
      }
      history.replace("/home");
    } catch {
      present({
        message: "No se pudo registrar la respuesta.",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setEnviando(false);
    }
  };

  /**
   * Primer toque en una opcion. "No voy a asistir" no se manda de una: abre el
   * bloque de motivo y espera. El resto se envia directo, como siempre.
   */
  const seleccionarOpcion = (opcion: OpcionRespuesta) => {
    if (enviando || respuestaEnviada) return;
    if (opcion.codigo === CODIGO_NO_ASISTIR) {
      setOpcionPendiente(opcion);
      return;
    }
    enviarRespuesta(opcion);
  };

  const cancelarMotivo = () => {
    setOpcionPendiente(null);
    setMotivoAusencia("");
    setMotivoTexto("");
  };

  /** Segundo toque: ya eligio el motivo y confirma el envio. */
  const confirmarNoAsistir = () => {
    if (!opcionPendiente) return;

    if (!motivoAusencia) {
      present({
        message: "Selecciona el motivo de ausencia.",
        duration: 3000,
        color: "warning",
      });
      return;
    }
    if (motivoAusencia === motivoOtro && !motivoTexto.trim()) {
      present({
        message: "Describe el motivo de tu ausencia.",
        duration: 3000,
        color: "warning",
      });
      return;
    }

    enviarRespuesta(opcionPendiente, {
      motivo_ausencia: motivoAusencia,
      ...(motivoAusencia === motivoOtro ? { motivo_texto: motivoTexto.trim() } : {}),
    });
  };

  const confirmarCancelacion = () => {
    if (enviando || respuestaEnviada) return;
    setConfirmarCerrar(true);
  };

  const cerrar = async () => {
    if (enviando || respuestaEnviada) return;
    setEnviando(true);
    try {
      await enviarRespuestaTardanza({
        ...datos,
        respuesta_codigo: 0,
        respuesta_texto: "Cancelado por el usuario",
        origen: "push",
      });
      setRespuestaEnviada(true);
      history.replace("/home");
    } catch {
      present({
        message: "No se pudo registrar el cierre.",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Notificacion de tardanza" />

      <IonContent>
        <IonLoading isOpen={enviando} message="Enviando respuesta..." />
        <IonAlert
          isOpen={confirmarCerrar}
          onDidDismiss={() => setConfirmarCerrar(false)}
          header="Confirmar cierre"
          message="Seguro que desea Cerrar sin Motivo"
          buttons={[
            {
              text: "No",
              role: "cancel",
              handler: () => setConfirmarCerrar(false),
            },
            {
              text: "Si",
              handler: () => cerrar(),
            },
          ]}
        />
        <div className="container mt-1">
          <div className="kpi-card">
            <div className="kpi-item-row">
              <IonText>
                {datos.op_nombre ? `Hola ${datos.op_nombre},` : "Hola,"}
              </IonText>
            </div>
            <div className="kpi-item-row">
              <IonText>
                No se registro tu marcacion de entrada. Selecciona una opcion.
              </IonText>
            </div>
            {descripcion && (
              <div className="kpi-item-row">
                <IonText>{descripcion}</IonText>
              </div>
            )}
          </div>

          <div className="mt-1">
            {OPCIONES.map((opcion) => (
              <IonButton
                key={opcion.codigo}
                expand="block"
                color={
                  opcionPendiente?.codigo === opcion.codigo ? "secondary" : "primary"
                }
                style={{ marginBottom: 10 }}
                disabled={
                  enviando ||
                  respuestaEnviada ||
                  (opcionPendiente !== null && opcionPendiente.codigo !== opcion.codigo)
                }
                onClick={() => seleccionarOpcion(opcion)}
              >
                {opcion.texto}
              </IonButton>
            ))}
          </div>

          {/* Motivo de la ausencia: solo para "no voy a asistir" */}
          {opcionPendiente && (
            <div style={{ marginTop: 12 }}>
              <IonText>
                <p style={{ fontWeight: 600, marginBottom: 4 }}>
                  Motivo de ausencia <span style={{ color: "red" }}>*</span>
                </p>
              </IonText>
              <IonList>
                <IonItem>
                  <IonLabel>Motivo</IonLabel>
                  <IonSelect
                    value={motivoAusencia}
                    placeholder="Selecciona..."
                    onIonChange={(e) => setMotivoAusencia(String(e.detail.value))}
                    interface="action-sheet"
                    disabled={enviando || respuestaEnviada}
                  >
                    {motivos.map((m) => (
                      <IonSelectOption key={m} value={m}>
                        {m}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
              </IonList>

              {motivoAusencia === motivoOtro && (
                <IonItem style={{ marginTop: 8 }}>
                  <IonLabel position="floating">
                    Describe el motivo <span style={{ color: "red" }}>*</span>
                  </IonLabel>
                  <IonTextarea
                    value={motivoTexto}
                    onIonChange={(e) => setMotivoTexto(e.detail.value || "")}
                    rows={3}
                    maxlength={300}
                    placeholder="Escribe aqui el motivo..."
                    disabled={enviando || respuestaEnviada}
                  />
                </IonItem>
              )}

              <IonButton
                expand="block"
                color="primary"
                style={{ marginTop: 12 }}
                disabled={enviando || respuestaEnviada}
                onClick={confirmarNoAsistir}
              >
                Enviar
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                color="medium"
                disabled={enviando || respuestaEnviada}
                onClick={cancelarMotivo}
              >
                Elegir otra opcion
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton
            expand="block"
            color="medium"
            disabled={enviando || respuestaEnviada}
            onClick={confirmarCancelacion}
          >
            Cerrar
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default NotificacionTardanza;
